import React from "react";
import { FiImage } from "react-icons/fi";

const HEX_RE = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const baseInputClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

export function Input({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  hint = "",
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className={baseInputClass}
      />

      {hint ? (
        <span className="mt-1.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function Textarea({
  label,
  name,
  value,
  onChange,
  rows = 4,
  placeholder = "",
  hint = "",
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <textarea
        name={name}
        value={value || ""}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className={`${baseInputClass} resize-y leading-7`}
      />

      {hint ? (
        <span className="mt-1.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function ColorInput({ label, name, value, onChange, hint = "" }) {
  const valid = HEX_RE.test(value || "");

  return (
    <label className="block rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <div className="flex min-w-0 items-center gap-3">
        <input
          type="color"
          name={name}
          value={valid ? value : "#000000"}
          onChange={onChange}
          className="h-11 w-11 shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 dark:border-slate-700"
        />

        <input
          type="text"
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder="#007AC9"
          className={`min-w-0 flex-1 rounded-2xl border bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:ring-2 dark:bg-slate-900 dark:text-slate-100 ${
            valid
              ? "border-slate-300 focus:border-sky-500 focus:ring-sky-100 dark:border-slate-700 dark:focus:ring-sky-950"
              : "border-rose-400 focus:border-rose-500 focus:ring-rose-100 dark:focus:ring-rose-950"
          }`}
        />
      </div>

      {hint ? (
        <span className="mt-2 block text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      ) : null}

      {!valid ? (
        <span className="mt-2 block text-xs font-medium text-rose-500">
          Format attendu : #RRGGBB ou #RGB
        </span>
      ) : null}
    </label>
  );
}

export function PhotoInput({
  label,
  name,
  value,
  onChange,
  onOpenLibrary,
  showPreview = true,
  previewMode = "cover",
  hint = "",
}) {
  const imgClass =
    previewMode === "contain" ? "object-contain" : "object-cover";

  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <div className="flex gap-2">
        <input
          type="text"
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder="https://… ou /uploads/…"
          className={baseInputClass}
        />

        {onOpenLibrary ? (
          <button
            type="button"
            onClick={onOpenLibrary}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-700 transition hover:bg-sky-100 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
          >
            <FiImage />
            <span className="hidden sm:inline">Photothèque</span>
          </button>
        ) : null}
      </div>

      {hint ? (
        <span className="mt-1.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      ) : null}

      {showPreview && value ? (
        <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 p-2 dark:border-slate-800 dark:bg-slate-950">
          <div className="h-32 w-full">
            <img
              src={value}
              alt="aperçu"
              className={`h-full w-full ${imgClass}`}
              loading="lazy"
            />
          </div>
        </div>
      ) : null}
    </label>
  );
}